import { useMemo } from "react";

export default function HeatmapMatrix({ cells, origins, destinations, zoneById }) {
  const { rows, cols, max, valueAt } = useMemo(() => {
    const map = new Map();
    let m = 0;
    cells.forEach((c) => {
      map.set(`${c.origin_id}::${c.destination_id}`, c.trips);
      if (c.trips > m) m = c.trips;
    });
    return {
      rows: origins,
      cols: destinations,
      max: m,
      valueAt: (o, d) => map.get(`${o}::${d}`) || 0,
    };
  }, [cells, origins, destinations]);

  if (!cells.length) return null;

  const cellColor = (v) => {
    if (v === 0) return "rgba(255,255,255,0.02)";
    const t = Math.min(1, v / max);
    // mix from cyan to orange
    const r = Math.round(0 + (255 - 0) * t);
    const g = Math.round(229 + (152 - 229) * t);
    const b = Math.round(255 + (0 - 255) * t);
    return `rgba(${r},${g},${b},${0.18 + 0.7 * t})`;
  };

  return (
    <div className="border border-white/10 bg-[#121212] p-4">
      <p className="label-micro mb-3">HEATMAP O/D</p>
      <div className="overflow-auto max-h-[60vh]">
        <table className="text-[10px] font-mono border-collapse">
          <thead>
            <tr>
              <th className="sticky left-0 top-0 z-20 bg-[#121212] border-b border-white/10 p-1 text-white/40 text-left min-w-[140px]">
                O \ D
              </th>
              {cols.map((d) => (
                <th
                  key={d}
                  className="sticky top-0 bg-[#121212] border-b border-white/10 p-1 text-white/40 text-left whitespace-nowrap"
                  style={{ minWidth: 36 }}
                  title={zoneById.get(d)?.name}
                >
                  <div style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
                    {zoneById.get(d)?.name?.split(" - ")[1] || zoneById.get(d)?.name?.slice(0, 12) || d}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o}>
                <td className="sticky left-0 bg-[#121212] border-r border-white/10 p-1 text-white/60 whitespace-nowrap pr-2">
                  {zoneById.get(o)?.name || o}
                </td>
                {cols.map((d) => {
                  const v = valueAt(o, d);
                  return (
                    <td
                      key={d}
                      style={{ backgroundColor: cellColor(v) }}
                      className="border border-black/40 text-center px-1"
                      title={`${zoneById.get(o)?.name} → ${zoneById.get(d)?.name}: ${v.toLocaleString("es-ES")} viajes`}
                    >
                      <span className="text-[9px] text-white/80">{v > 0 ? Math.round(v) : ""}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex items-center gap-3 text-[10px] font-mono text-white/40">
        <span>0</span>
        <div className="h-2 flex-1 max-w-[200px]" style={{ background: "linear-gradient(90deg, rgba(0,229,255,0.18), #FF9800)" }} />
        <span>{Math.round(max).toLocaleString("es-ES")}</span>
      </div>
    </div>
  );
}
